import { INotificationAction } from '../interfaces/notification-action'
import { parseNotificationResponse } from '../features/notifications/helpers/notification-helpers'
import { INotificationFieldSettings } from '@pushly/models/lib/interfaces'
import { SubjectEntity } from '../enums/ability-entity.enum'
import type { ICaslSubject } from '../stores/app-ability'
import { IAddedNotificationQueryParam } from '../interfaces/added-notification-query-param'
import {
    IAbstractNativeApnsSendIntegration,
    IAbstractNativeFcmSendIntegration,
    IWebApnsSendIntegration,
} from '../interfaces/send-integrations'
import { FEAT_CHANNEL_ANDROID, FEAT_CHANNEL_IOS, FEAT_CHANNEL_WEB, FEAT_NOTIF_FIELD_SUGGESTIONS } from '../constants'
import { getEnabledDeliveryChannels } from '../_utils/domain'
import { CatalogItemType } from '@pushly/models/lib/enums'
import { DomainDraftRetention, DomainDuplicateTimeWindow } from '@pushly/models/lib/structs/domains'

export interface IDomainUtmParams {
    utm_source: string
    utm_medium?: string
    utm_campaign?: string
    utm_term?: string
    utm_content?: string
}

export type DomainFrequencyCapMetric = 'days' | 'hours' | 'minutes'

export interface IDomainFrequencyCap {
    occurrences: number
    fcapSeconds: number
    displayMetric: DomainFrequencyCapMetric
}

export interface IDomainFrequencyCaps {
    prompt?: IDomainFrequencyCap
    notification?: IDomainFrequencyCap
}

export class DomainKeyword {
    public readonly id: number
    public readonly name: string
}

export class DomainPageTag {
    public readonly id: number
    public readonly name: string
}

export class EcommItemGroup {
    public readonly id: number
    public readonly name: string
}

export enum PromptReferrerAllowType {
    ALLOW = 'allow',
    IGNORE = 'ignore',
    BLOCK = 'block',
}

export interface IPromptReferrerUrlsConfig {
    exact?: string[]
    contains?: string[]
}

export interface IGlobalPromptReferrerConditions {
    enabled: boolean
    persistTtlSeconds: number
    whenSelf: PromptReferrerAllowType
    whenBlank: PromptReferrerAllowType
    allowed?: IPromptReferrerUrlsConfig
    blocked?: IPromptReferrerUrlsConfig
}

export interface IDomainEcommConfig {
    clickAttributionWindowHours?: number
    impressionAttributionWindowHours?: number
    maxAbandonedItemAgeDays?: number
    itemType?: CatalogItemType
    catalogLocales?: string[]
    catalogDefaultLocale?: string
}

export interface IDomainGlobalPromptSettings {
    referrers?: IGlobalPromptReferrerConditions
}

export class DomainDto implements ICaslSubject<SubjectEntity.DOMAIN> {
    public static fromApiResponse(fields: Partial<DomainDto>): DomainDto {
        const dto = new DomainDto()
        Object.assign(dto, fields)

        /**
         * Hydrated here rather than left as the raw response object, so that
         * `duplicateTimeWindow` is genuinely a struct wherever the DTO is consumed. The
         * nested `landingUrlMatch` block only survives as a struct through
         * `DomainDuplicateTimeWindow.from`, and aqe's display guards on
         * `isDomainDuplicateTimeWindow` - a plain object silently renders as disabled.
         *
         * `from` accepts snake_case and camelCase input and is idempotent on an already
         * hydrated struct, so this is safe regardless of what the caller passed.
         */
        if (dto.duplicateTimeWindow) {
            dto.duplicateTimeWindow = DomainDuplicateTimeWindow.from(dto.duplicateTimeWindow)
        }

        if (dto.draftRetention) {
            dto.draftRetention = DomainDraftRetention.from(dto.draftRetention)
        }

        if (dto.fallbackNotificationTemplate) {
            try {
                dto.fallbackNotificationTemplate = parseNotificationResponse(dto.fallbackNotificationTemplate)
            } catch (err) {
                console.warn('Invalid fallback notification template', dto.fallbackNotificationTemplate)
                console.error(err)
            }
        }

        return dto
    }

    public __caslSubjectType__ = SubjectEntity.DOMAIN

    public readonly id: number
    public accountName: string
    public accountId: number
    public name: string // DOMAIN NAME - eg: seaworld.com
    public displayName: string // DISPLAY NAME - eg: Seaworld
    public timezone: string
    public currencyCode: string
    public currency?: {
        code: string
        name: string
    }
    public domainKey: string
    public statusId: number
    public status: string
    public senderId: string
    public fcmServerKey: string
    public vapidPublicKey: string
    public defaultIconUrl: string
    public defaultBadgeUrl: string
    public defaultNotificationTtlSeconds: number
    public globalPromptSettings?: IDomainGlobalPromptSettings
    public ecommConfig?: IDomainEcommConfig
    public welcomeNotificationEnabled: boolean
    public utmParams: IDomainUtmParams
    public flags: string[] = []
    public accountFlags: string[] = []
    public frequencyCaps: IDomainFrequencyCaps
    public createdAt: Date
    public updatedAt: Date
    public apnsConfiguration: IWebApnsSendIntegration
    public nativeApnsConfiguration: IAbstractNativeApnsSendIntegration
    public nativeFcmConfiguration: IAbstractNativeFcmSendIntegration
    public whitelistDomains: string[]
    public sdkEventOnlyDomains: string[]
    public landingUrlDomainWhitelist: string[]
    /**
     * Optional because `fromApiResponse` only assigns it when the key is present, so it
     * is genuinely undefined for any payload that omits it - including cached domain JSON
     * written before the block existed. Declaring it non-optional made strictNullChecks
     * silent at call sites that did not happen to use `?.`.
     */
    public duplicateTimeWindow?: DomainDuplicateTimeWindow
    public draftRetention?: DomainDraftRetention
    public landingUrlValidation?: { enabled: boolean }
    public mediaQuality?: number
    public displayMeta: any
    public defaultNotificationActionLabels: any = {}
    public defaultNotificationActions: INotificationAction[] = []
    public fallbackNotificationTemplateId: string
    public fallbackNotificationTemplate: any
    public notificationFieldSettings: INotificationFieldSettings | null

    public addedNotificationQueryParams: { [key: string]: IAddedNotificationQueryParam }
    public collapseKeyType: string
    public routingKey: string

    public readonly createdBy: number
    public readonly updatedBy: number
    public createdByUsername: string
    public updatedByUsername: string

    public readonly prodDomainId: number

    public isDemo: boolean
    public isDemoTemplate: boolean
    /**
     * Whether the owning organization is a demo organization. Role grants for demo
     * content are conditioned on it, so it must be present on the domain the app
     * evaluates abilities against; an absent value reads as false.
     */
    public accountIsDemo: boolean = false
    /**
     * Whether the owning organization is a demo template. Grants for demo organizations require
     * it to be false, so it is evaluated together with `accountIsDemo`; an absent value reads
     * as false.
     */
    public accountIsDemoTemplate: boolean = false
    public sourceDemoTemplateDomainId: number | null

    public get isDemoDomain(): boolean {
        // Use isDemo column if available, fall back to legacy flag check
        if (this.isDemo !== undefined) {
            return this.isDemo
        }
        return this.flags?.includes('DEMO') ?? false
    }

    public get isInternal(): boolean {
        return (this.flags?.includes('INTERNAL') ?? false) || (this.accountFlags?.includes('INTERNAL') ?? false)
    }

    /**
     * A non-production domain — a test or dev copy that points at the production
     * domain it shadows. Nothing sent from one reaches a real audience.
     */
    public get isTestDomain(): boolean {
        return !!this.prodDomainId
    }

    public get isPreviewDomain(): boolean {
        return this.flags?.includes('PREVIEW_DOMAIN') ?? false
    }

    // Channel enablement and page analysis are entity flags: the flag catalog does not gate them.

    /**
     * Whether Page Analysis (landing URL content suggestions) is switched on for this domain.
     * Which fields it may fill is a separate setting, `notificationFieldSettings.content_suggestions`.
     */
    public get hasPageAnalysis(): boolean {
        return this.flags?.includes(FEAT_NOTIF_FIELD_SUGGESTIONS) ?? false
    }

    public get isInternalAccountDomain(): boolean {
        return this.accountFlags?.includes('INTERNAL') ?? false
    }

    public isWebEnabled(): boolean {
        return this.flags?.includes(FEAT_CHANNEL_WEB) ?? false
    }

    public get activeChannels() {
        return getEnabledDeliveryChannels(this, true)
    }

    public isIOSEnabled(): boolean {
        return (this.flags?.includes(FEAT_CHANNEL_IOS) ?? false) && this.nativeApnsConfiguration?.isActive
    }

    public isAndroidEnabled(): boolean {
        return (this.flags?.includes(FEAT_CHANNEL_ANDROID) ?? false) && this.nativeFcmConfiguration?.isActive
    }
}
